import clsx from 'clsx'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, query, queryAssignedElements } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'
import { translate } from 'lit-i18n'

import { DdsElement } from '@/internal/dds-hu-element'

import DapDSCard from '../card/card.component'
import DapDSCardContent from '../card/card-content.component'
import DapDSIcon from '../icon/icon.component'
import DapDSIconButton from '../icon-button/icon-button.component'
import styles from './banner.scss?inline'

export type BannerTheme = 'clean' | 'normal'

export type BannerVariant =
  | 'brand'
  | 'positive'
  | 'info'
  | 'warning'
  | 'negative'

const iconNameMap = {
  brand: 'information-2-fill',
  info: 'information-fill',
  positive: 'checkbox-circle-fill',
  warning: 'alert-fill',
  negative: 'error-warning-fill',
}

/**
 * `dap-ds-banner` is a custom banner component.
 * @element dap-ds-banner
 * @summary A banner is a message displayed at the top of the page to provide important information to the user.
 * @title - Banner
 *
 * @event {{ void }} dds-close - Event fired when the banner is closed.
 *
 * @slot - The content of the banner.
 * @slot actions - Actions of banner
 * @slot icon - The icon of the banner.
 *
 * @csspart base - The main banner container.
 * @csspart card-base - The wrapper card container.
 * @csspart icon - The icon of the banner.
 * @csspart icon-element - The icon element of the banner.
 * @csspart icon-base - The base of the icon.
 * @csspart closebutton - The close button of the banner.
 * @csspart close-icon-element - The icon element of the close button.
 * @csspart close-icon-base - The base of the close button icon.
 * @csspart actions - The actions of the banner.
 * @csspart title -  The title of the banner.
 *
 * @cssproperty --dds-banner-gap - The gap between banner elements (default: var(--dds-spacing-200))
 * @cssproperty --dds-banner-line-height - The line height of the banner text (default: var(--dds-font-line-height-xlarge))
 * @cssproperty --dds-banner-transition - The transition property for the banner (default: all 0.2s ease-in-out)
 *
 * @cssproperty --dds-banner-brand-background - The background color of the brand banner (default: var(--dds-banner-background-brand))
 * @cssproperty --dds-banner-brand-icon-color - The color of the brand banner icon (default: var(--dds-banner-icon-brand))
 * @cssproperty --dds-banner-brand-text-color - The text color of the brand banner (default: var(--dds-banner-text-brand))
 * @cssproperty --dds-banner-brand-action-color - The color of the brand banner actions (default: var(--dds-banner-action-enabled))
 *
 * @cssproperty --dds-banner-info-background - The background color of the info banner (default: var(--dds-banner-background-informative))
 * @cssproperty --dds-banner-info-icon-color - The color of the info banner icon (default: var(--dds-banner-icon-informative))
 * @cssproperty --dds-banner-info-text-color - The text color of the info banner (default: var(--dds-banner-text-informative))
 * @cssproperty --dds-banner-info-action-color - The color of the info banner actions (default: var(--dds-banner-action-enabled))
 *
 * @cssproperty --dds-banner-positive-background - The background color of the positive banner (default: var(--dds-banner-background-positive))
 * @cssproperty --dds-banner-positive-icon-color - The color of the positive banner icon (default: var(--dds-banner-icon-positive))
 * @cssproperty --dds-banner-positive-text-color - The text color of the positive banner (default: var(--dds-banner-text-positive))
 * @cssproperty --dds-banner-positive-action-color - The color of the positive banner actions (default: var(--dds-banner-action-enabled))
 *
 * @cssproperty --dds-banner-warning-background - The background color of the warning banner (default: var(--dds-banner-background-warning))
 * @cssproperty --dds-banner-warning-icon-color - The color of the warning banner icon (default: var(--dds-banner-icon-warning))
 * @cssproperty --dds-banner-warning-text-color - The text color of the warning banner (default: var(--dds-banner-text-warning))
 * @cssproperty --dds-banner-warning-action-color - The color of the warning banner actions (default: var(--dds-banner-action-inverted-enabled))
 *
 * @cssproperty --dds-banner-negative-background - The background color of the negative banner (default: var(--dds-banner-background-negative))
 * @cssproperty --dds-banner-negative-icon-color - The color of the negative banner icon (default: var(--dds-banner-icon-negative))
 * @cssproperty --dds-banner-negative-text-color - The text color of the negative banner (default: var(--dds-banner-text-negative))
 * @cssproperty --dds-banner-negative-action-color - The color of the negative banner actions (default: var(--dds-banner-action-inverted-enabled))
 */
export default class DapDSBanner extends DdsElement {
  static tagName = 'dap-ds-banner'

  static dependencies = {
    'dap-ds-card': DapDSCard,
    'dap-ds-card-content': DapDSCardContent,
    'dap-ds-icon': DapDSIcon,
    'dap-ds-icon-button': DapDSIconButton,
  }

  /** @ignore */
  @queryAssignedElements({ slot: 'actions' }) actionSlot: HTMLElement[]
  /** @ignore */
  @query('.banner__actions') actionContainer: HTMLElement

  /** The variant of the banner
   * @type { 'brand' | 'positive' | 'info' | 'warning' | 'negative' }
   */
  @property() variant: BannerVariant = 'brand'

  /** Whether the banner is closeable */
  @property({ type: Boolean }) closeable: boolean

  /** State of the banner. If false banner is hidden */
  @property({ reflect: true }) opened: string = 'true'

  /** The aria-label for the close button */
  @property({ type: String })
  closeButtonLabel = 'close'

  /** `data-testid` for the close button. */
  @property() closeButtonTestId = 'banner-close-button'

  /** The icon of the banner, this is a name of a built icon icon */
  @property({ type: String }) icon: string

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties:
      | PropertyValueMap<DapDSBanner>
      | Map<PropertyKey, unknown>,
  ): void {
    if (this.actionSlot?.length) {
      if (this.actionSlot?.length && this.variant !== 'warning') {
        this.actionSlot.forEach(element => {
          element.setAttribute('variant', 'inverted')
        })
      } else {
        this.actionSlot.forEach(element => {
          element.setAttribute('variant', 'warning')
        })
      }
    }

    if (this.actionSlot && !this.actionSlot.length && this.actionContainer) {
      this.actionContainer.style.display = 'none'
    }
  }

  private onCloseClick() {
    this.emit('dds-close')
  }

  render() {
    const icon = iconNameMap[this.variant]

    if (this.opened === 'false') {
      return nothing
    }
    return html`
      <dap-ds-card size="lg">
        <dap-ds-card-content
          class=${clsx(`banner--${this.variant}`)}
          part="card-base"
          spacing="none">
          <div class=${clsx('banner', `banner--${this.variant}`)} part="base">
            <div class="banner__icon-container">
              ${this.icon || icon
                ? html`<slot part="icon" name="icon">
                    <dap-ds-icon
                      part="icon"
                      exportparts="icon:icon-element, base:icon-base"
                      class="banner__icon"
                      aria-hidden="true"
                      name="${this.icon || icon}">
                    </dap-ds-icon
                  ></slot>`
                : html`<slot name="icon" part="icon"></slot>`}
            </div>
            <div>
              <slot class="banner__content"></slot>

              <div class="banner__actions">
                <slot name="actions" part="actions"></slot>
              </div>
            </div>

            ${this.closeable
              ? html`
                  <dap-ds-icon-button
                    class="banner__close"
                    part="close-button"
                    data-testid=${this.closeButtonTestId}
                    exportparts="icon-base:close-icon-element, base:close-icon-base"
                    variant="inverted"
                    clean
                    size="md"
                    icon="close-line"
                    part="closebutton"
                    @click="${this.onCloseClick}"
                    aria-label=${this.closeButtonLabel ?? translate('close')}>
                  </dap-ds-icon-button>
                `
              : nothing}
          </div>
        </dap-ds-card-content>
      </dap-ds-card>
    `
  }
}
